import { useEffect } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import {
  fetchPreview,
  selectPreview,
  selectRules,
  selectRoutingConsultants,
} from "@features/lead-routing/leadRouting.slice"
import { PreviewConsultant } from "@features/lead-routing/leadRouting.types"
import { chipTintSx } from "./leadRoutingUi"

function statusChip(row: PreviewConsultant) {
  if (row.eligible) {
    return <Chip size="small" label="eligible" sx={chipTintSx("good")} />
  }
  const label =
    row.reason === "off_shift"
      ? "off shift"
      : row.reason === "filtered"
      ? "filtered"
      : row.reason === "removed"
      ? "removed"
      : "not eligible"
  return <Chip size="small" color="default" variant="outlined" label={label} />
}

export function PreviewSection(props: { reloadKey?: number }) {
  const dispatch = useAppDispatch()
  const previewState = useAppSelector(selectPreview)
  const consultantsState = useAppSelector(selectRoutingConsultants)
  const rulesState = useAppSelector(selectRules)

  // Refetches whenever the rules change (reorder, publish, edit, delete) so
  // this view always reflects the rules as they stand now.
  const rulesVersion =
    rulesState.status === "success"
      ? rulesState.data
          .map(
            (rule) =>
              `${rule.id}:${rule.priority}:${rule.published}:${rule.disabled}:${rule.hasUnpublishedChanges}`,
          )
          .join("|")
      : ""
  useEffect(() => {
    dispatch(fetchPreview())
  }, [dispatch, rulesVersion, props.reloadKey])

  const consultants =
    consultantsState.status === "success" ? consultantsState.data : []
  const consultantName = (id: string) =>
    consultants.find((c) => c.id === id)?.name ?? id.slice(0, 8)

  return (
    <Box mb={3}>
      <Typography variant="subtitle1">If a lead arrived now</Typography>
      <Typography variant="caption" color="text.secondary" component="p" mb={1}>
        Leads are assigned the instant they arrive, nothing queues. This shows
        each live rule&apos;s split at this moment, given who is on shift and
        eligible.
      </Typography>

      {previewState.status === "error" && (
        <Alert severity="error">Failed to load the preview.</Alert>
      )}

      {(previewState.status === "idle" ||
        previewState.status === "pending") && (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      )}

      {previewState.status === "success" &&
        previewState.data.rules.length === 0 && (
          <Alert severity="info">
            No published rules with weighted consultants yet, publish a rule to
            preview its split.
          </Alert>
        )}

      {previewState.status === "success" &&
        previewState.data.rules.map((rule) => (
          <Accordion
            key={rule.ruleId}
            variant="outlined"
            disableGutters
            sx={{ mb: 0.75, "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 0,
                "& .MuiAccordionSummary-content": { my: 0.75 },
                "&.Mui-expanded": { minHeight: 0 },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: "100%", pr: 1 }}
              >
                <Typography variant="body2">{rule.ruleName}</Typography>
                {rule.holdLead ? (
                  <Typography variant="caption" color="warning.main">
                    holds leads — nobody is assigned
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color={
                      rule.ineligibleCount > 0
                        ? "warning.main"
                        : "text.secondary"
                    }
                  >
                    {rule.eligibleCount} of{" "}
                    {rule.eligibleCount + rule.ineligibleCount} eligible now
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Consultant</TableCell>
                    <TableCell align="right">
                      <Tooltip title="The share this consultant is targeted for in this rule's split — a set weight, or an even split of the tag's members when unweighted.">
                        <span>Target</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="The share this consultant would get if a lead arrived now, counting only who is on shift and eligible. Each goal share is rescaled so the available consultants add up to 100%.">
                        <span>Would get now</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rule.distribution.map((row) => (
                    <TableRow key={row.consultantId}>
                      <TableCell>{consultantName(row.consultantId)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {row.targetPct}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                          color={
                            row.eligible ? "text.primary" : "text.disabled"
                          }
                        >
                          {row.wouldBePct}%
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  )
}
